import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Paper,
  Collapse,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Alert,
} from "@mui/material";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  useGetConfigDetailsQuery,
  useGetListOfTagsQuery,
  useCreateTagMutation,
} from "@/features/bughurd/api";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import {
  useGetProjectOrReposForImportQuery,
  useGetRepoBranchesMutation,
} from "@/features/CompanyInformation/companyApi";

const applicationTypeOptions = [
  { key: "custom", label: "Custom", icon: "⌨", desc: "Code repositories only" },
  { key: "saas", label: "SaaS", icon: "☁", desc: "Cloud platform binding" },
  { key: "hybrid", label: "Hybrid", icon: "⚙", desc: "Repos + SaaS platform" },
];

const emptyRepo = () => ({
  url: "",
  branch: "main",
  description: "",
  currentRelease: "",
  incomingRelease: "",
});

const emptyApp = () => ({
  name: "",
  description: "",
  type: "custom",
  repos: [emptyRepo()],
  platform: "",
  platformVersion: "",
  envUrl: "",
  orgId: "",
});

interface Repo {
  url: string;
  branch: string;
  description: string;
  currentRelease: string;
  incomingRelease: string;
}

interface Application {
  name: string;
  description: string;
  type: string;
  repos: Repo[];
  platform: string;
  platformVersion: string;
  envUrl: string;
  orgId: string;
}

interface ApplicationSectionProps {
  applications: Application[];
  onChange: (applications: Application[]) => void;
  projectId?: string;
}

export default function ApplicationSection({
  applications,
  onChange,
  projectId,
}: ApplicationSectionProps) {
  const { t } = useTranslation(["bugherd"]);
  const navigate = useNavigate();
  const [expandedApp, setExpandedApp] = useState<number | null>(null);
  const [platformInput, setPlatformInput] = useState("");
  const [isCreatingPlatform, setIsCreatingPlatform] = useState(false);

  // Tags API for platform options (filtered by type=APPLICATION)
  const { data: tagList, refetch: refetchTags } = useGetListOfTagsQuery(
    { projectId: projectId || "", type: "APPLICATION" },
    { skip: !projectId },
  );
  const DEFAULT_PLATFORMS = ["Salesforce", "ServiceNow", "Workday", "SAP"];
  const availablePlatforms = tagList?.data?.decryptedData?.items || [];
  const apiPlatforms: string[] = availablePlatforms.map((t: any) => t.tagName);
  const platformOptions: string[] = Array.from(
    new Set([...DEFAULT_PLATFORMS, ...apiPlatforms]),
  );
  const [createTag] = useCreateTagMutation();

  const { data: configData, isLoading: configLoading } =
    useGetConfigDetailsQuery({}, { refetchOnMountOrArgChange: true });

  console.log("Config Data:", configData);

  const isGitConfigured =
    Array.isArray(configData?.data?.decryptedData) &&
    configData?.data?.decryptedData?.some(
      (config: any) => config.type === "GIT_HUB",
    );

  const {
    data: repoData,
    isLoading: repoDataLoading,
    error,
  } = useGetProjectOrReposForImportQuery("GIT_HUB", {
    skip: !isGitConfigured,
  });
  const [getRepoBranches] = useGetRepoBranchesMutation();

  // Store branch data per repo key (appIndex-repoIndex)
  const [branchDataMap, setBranchDataMap] = useState<
    Record<string, { data: any[]; loading: boolean }>
  >({});

  const fetchBranches = async (
    appIndex: number,
    repoIndex: number,
    repoName: string,
  ) => {
    const key = `${appIndex}-${repoIndex}`;
    setBranchDataMap((prev) => ({
      ...prev,
      [key]: { data: prev[key]?.data || [], loading: true },
    }));
    try {
      const res: any = await getRepoBranches({
        repoName,
        from: "GIT_HUB",
      }).unwrap();
      setBranchDataMap((prev) => ({
        ...prev,
        [key]: {
          data: res?.data?.decryptedData?.data || [],
          loading: false,
        },
      }));
    } catch {
      setBranchDataMap((prev) => ({
        ...prev,
        [key]: { data: [], loading: false },
      }));
    }
  };

  const addApplication = () => {
    const newApps = [...applications, emptyApp()];
    onChange(newApps);
    setExpandedApp(newApps.length - 1);
  };

  const updateApplication = (index: number, updated: Application) => {
    const newApps = [...applications];
    newApps[index] = updated;
    onChange(newApps);
  };

  const removeApplication = (index: number) => {
    const newApps = [...applications];
    newApps.splice(index, 1);
    onChange(newApps);
    if (expandedApp === index) setExpandedApp(null);
    else if (expandedApp !== null && expandedApp > index)
      setExpandedApp(expandedApp - 1);
  };

  const addRepo = (appIndex: number) => {
    const app = { ...applications[appIndex] };
    app.repos = [...app.repos, emptyRepo()];
    updateApplication(appIndex, app);
  };

  const updateRepo = (appIndex: number, repoIndex: number, repo: Repo) => {
    const app = { ...applications[appIndex] };
    const repos = [...app.repos];
    repos[repoIndex] = repo;
    app.repos = repos;
    updateApplication(appIndex, app);
  };

  const removeRepo = (appIndex: number, repoIndex: number) => {
    const app = { ...applications[appIndex] };
    const repos = [...app.repos];
    repos.splice(repoIndex, 1);
    app.repos = repos;
    updateApplication(appIndex, app);
  };

  const totalRepos = applications.reduce(
    (sum, app) => sum + (app.repos?.length || 0),
    0,
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2.5,
        borderRadius: "12px",
        border: (theme) => `1px solid ${theme.palette.divider}`,
        bgcolor: (theme) => theme.palette.background.paper,
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 1px 8px rgba(0,0,0,0.2)"
            : "0 1px 8px rgba(0,0,0,0.04)",
      }}
    >
      {/* Section Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        pb={1.5}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: (theme) => `${theme.palette.primary.main}1A`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppsOutlinedIcon
              sx={{
                fontSize: 17,
                color: (theme) => theme.palette.primary.main,
              }}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle2" fontWeight={600} fontSize="13.5px">
              {t("Applications")}
            </Typography>
            <Chip
              label={`${applications.length} ${applications.length === 1 ? "app" : "apps"}`}
              size="small"
              sx={{ fontSize: "10px", height: 20 }}
            />
            <Chip
              label={`${totalRepos} ${totalRepos === 1 ? "repo" : "repos"}`}
              size="small"
              sx={{ fontSize: "10px", height: 20 }}
            />
          </Box>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={addApplication}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12px",
            borderRadius: "7px",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {t("Add Application")}
        </Button>
      </Box>

      {/* Info Banner */}
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: "10px",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(59,130,246,0.08) 100%)"
              : "linear-gradient(135deg, #ede9fe 0%, #dbeafe 100%)",
          border: (theme) =>
            `1px solid ${theme.palette.mode === "dark" ? "rgba(139,92,246,0.2)" : "#c4b5fd"}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(139,92,246,0.2)"
                : "rgba(139,92,246,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 16, lineHeight: 1 }}>✦</Typography>
        </Box>
        <Box>
          <Box display="flex" alignItems="center" gap={0.8} mb={0.2}>
            <Typography
              variant="caption"
              fontWeight={700}
              fontSize="11px"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#a78bfa" : "#7c3aed",
                letterSpacing: 0.3,
              }}
            >
              {t("AI-Powered Context")}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            fontSize="11px"
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.6)"
                  : "#4b5563",
              lineHeight: 1.4,
            }}
            display="block"
          >
            {t("Project → Applications → Repositories")} —{" "}
            {t(
              "Descriptions at every level help AI generate accurate bug analysis, solutions & test scripts.",
            )}
          </Typography>
        </Box>
      </Box>

      {/* Empty State */}
      {applications.length === 0 && (
        <Box
          onClick={addApplication}
          sx={{
            border: (theme) => `2px dashed ${theme.palette.primary.main}40`,
            borderRadius: "10px",
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            bgcolor: (theme) => `${theme.palette.primary.main}08`,
            transition: "all 0.2s",
            "&:hover": {
              bgcolor: (theme) => `${theme.palette.primary.main}12`,
              borderColor: (theme) => theme.palette.primary.main,
            },
          }}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary"
            fontSize="13px"
          >
            {t("Add your first application")}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontSize="11px">
            {t("e.g., Customer Portal, Admin Dashboard, Mobile API")}
          </Typography>
        </Box>
      )}

      {/* Application Accordions */}
      {applications.map((app, appIndex) => (
        <Paper
          key={appIndex}
          elevation={0}
          sx={{
            mb: 1.5,
            borderRadius: "10px",
            border: (theme) =>
              `1px solid ${expandedApp === appIndex ? theme.palette.primary.main + "60" : theme.palette.divider}`,
            overflow: "hidden",
            transition: "all 0.2s",
          }}
        >
          {/* Accordion Header */}
          <Box
            onClick={() =>
              setExpandedApp(expandedApp === appIndex ? null : appIndex)
            }
            sx={{
              p: "10px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              bgcolor: (theme) =>
                expandedApp === appIndex
                  ? `${theme.palette.primary.main}08`
                  : "transparent",
              transition: "background 0.15s",
              "&:hover": {
                bgcolor: (theme) => `${theme.palette.primary.main}06`,
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1.2}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                A{appIndex + 1}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  fontSize="13px"
                  color={app.name ? "text.primary" : "text.disabled"}
                  sx={{ fontStyle: app.name ? "normal" : "italic" }}
                >
                  {app.name || t("Untitled Application")}
                </Typography>
                <Box display="flex" gap={0.5} mt={0.3}>
                  <Chip
                    label={`${app.repos?.length || 0} ${(app.repos?.length || 0) === 1 ? "repo" : "repos"}`}
                    size="small"
                    sx={{ fontSize: "9px", height: 18 }}
                  />
                  <Chip
                    label={
                      applicationTypeOptions.find((t) => t.key === app.type)
                        ?.label || "Custom"
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: "9px", height: 18 }}
                  />
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={t("Remove application")}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeApplication(appIndex);
                  }}
                  sx={{ color: "error.main", fontSize: 14 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <ExpandMoreIcon
                sx={{
                  fontSize: 20,
                  color: "text.secondary",
                  transition: "transform 0.2s",
                  transform:
                    expandedApp === appIndex
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                }}
              />
            </Box>
          </Box>

          {/* Accordion Content */}
          <Collapse in={expandedApp === appIndex}>
            <Box
              sx={{
                p: 2,
                pt: 1,
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* App Name */}
              <Box mb={2}>
                <TextField
                  label={t("Application Name")}
                  fullWidth
                  size="medium"
                  value={app.name}
                  onChange={(e) =>
                    updateApplication(appIndex, {
                      ...app,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., Customer Portal"
                  required
                />
              </Box>

              {/* Application Type */}
              <Box mb={2}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  fontSize="11px"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 1,
                    display: "block",
                  }}
                >
                  {t("Application Type")}{" "}
                  <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>
                <Box display="flex" gap={1.2}>
                  {applicationTypeOptions.map((opt) => {
                    const isSelected = app.type === opt.key;
                    return (
                      <Box
                        key={opt.key}
                        onClick={() =>
                          updateApplication(appIndex, {
                            ...app,
                            type: opt.key,
                          })
                        }
                        sx={{
                          flex: 1,
                          py: 1.5,
                          px: 1.5,
                          textAlign: "center",
                          borderRadius: "10px",
                          cursor: "pointer",
                          position: "relative",
                          border: (theme) =>
                            `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                          bgcolor: (theme) =>
                            isSelected
                              ? `${theme.palette.primary.main}0D`
                              : "transparent",
                          boxShadow: isSelected
                            ? "0 2px 8px rgba(99,102,241,0.15)"
                            : "none",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: (theme) => theme.palette.primary.main,
                            bgcolor: (theme) =>
                              isSelected
                                ? `${theme.palette.primary.main}14`
                                : `${theme.palette.action.hover}`,
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </Box>
                        )}
                        <Typography
                          sx={{ fontSize: 22, mb: 0.5, lineHeight: 1 }}
                        >
                          {opt.icon}
                        </Typography>
                        <Typography
                          fontSize="12px"
                          fontWeight={700}
                          color={isSelected ? "primary.main" : "text.primary"}
                          sx={{ mb: 0.3 }}
                        >
                          {t(opt.label)}
                        </Typography>
                        <Typography
                          fontSize="10px"
                          fontWeight={400}
                          color="text.secondary"
                          sx={{ lineHeight: 1.3 }}
                        >
                          {t(opt.desc)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Description */}
              <Box mb={2}>
                <TextField
                  label={t("Description")}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={app.description}
                  onChange={(e) =>
                    updateApplication(appIndex, {
                      ...app,
                      description: e.target.value,
                    })
                  }
                  placeholder="e.g., Customer-facing portal for account management, billing, support."
                  helperText={
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.6,
                        mt: 0.3,
                        px: 1,
                        py: 0.3,
                        borderRadius: "4px",
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(139,92,246,0.1)"
                            : "linear-gradient(90deg, #ede9fe, #f3e8ff)",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "9px",
                          fontWeight: 800,
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#a78bfa"
                              : "#7c3aed",
                          letterSpacing: 0.5,
                        }}
                      >
                        ✦ AI
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "10px",
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#c4b5fd"
                              : "#6d28d9",
                          fontWeight: 500,
                        }}
                      >
                        {t(
                          "Better descriptions produce more accurate bug analysis, solutions & test scripts.",
                        )}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* SaaS Platform Binding — shown for saas & hybrid */}
              {(app.type === "saas" || app.type === "hybrid") && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: "8px",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(16,185,129,0.08)"
                        : "#d1fae5",
                    border: (theme) =>
                      `1px solid ${theme.palette.mode === "dark" ? "rgba(16,185,129,0.2)" : "#6ee7b733"}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    fontSize="11px"
                    color="success.main"
                    sx={{ mb: 1.5, display: "block" }}
                  >
                    ☁ {t("SaaS Platform Binding")}
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      fullWidth
                      options={platformOptions}
                      value={app.platform || ""}
                      inputValue={platformInput}
                      onInputChange={(_, newValue) =>
                        setPlatformInput(newValue)
                      }
                      onChange={async (_, newValue) => {
                        const val = (newValue as string) || "";
                        if (
                          val &&
                          !platformOptions.includes(val) &&
                          projectId
                        ) {
                          setIsCreatingPlatform(true);
                          try {
                            await createTag({
                              project: projectId,
                              tagName: val,
                              type: "APPLICATION",
                            }).unwrap();
                            await refetchTags();
                          } catch (e) {
                            console.error("Failed to create platform tag:", e);
                          }
                          setIsCreatingPlatform(false);
                        }
                        updateApplication(appIndex, { ...app, platform: val });
                      }}
                      filterOptions={(options, params) => {
                        const filtered = options.filter((o) =>
                          o
                            .toLowerCase()
                            .includes(params.inputValue.toLowerCase()),
                        );
                        if (
                          params.inputValue &&
                          !filtered.includes(params.inputValue)
                        ) {
                          filtered.push(params.inputValue);
                        }
                        return filtered;
                      }}
                      renderOption={(props, option) => {
                        const isNew = !platformOptions.includes(option);
                        return (
                          <li {...props}>
                            {isNew ? (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <AddCircleOutlineOutlinedIcon
                                  sx={{ fontSize: 16, color: "primary.main" }}
                                />
                                <Typography fontSize="13px">
                                  {t("Create")} "{option}"
                                </Typography>
                              </Box>
                            ) : (
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CheckIcon
                                  sx={{
                                    fontSize: 16,
                                    visibility:
                                      option === app.platform
                                        ? "visible"
                                        : "hidden",
                                  }}
                                />
                                <Typography fontSize="13px">
                                  {option}
                                </Typography>
                              </Box>
                            )}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("Platform")}
                          placeholder="e.g., Salesforce"
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isCreatingPlatform ? (
                                  <CircularProgress size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <TextField
                      label={t("Current Release")}
                      size="small"
                      fullWidth
                      required
                      value={app.platformVersion || ""}
                      onChange={(e) =>
                        updateApplication(appIndex, {
                          ...app,
                          platformVersion: e.target.value,
                        })
                      }
                      placeholder="e.g., Spring '25"
                    />
                    <TextField
                      label={t("Environment URL")}
                      size="small"
                      fullWidth
                      required
                      value={app.envUrl || ""}
                      onChange={(e) =>
                        updateApplication(appIndex, {
                          ...app,
                          envUrl: e.target.value,
                        })
                      }
                      placeholder="https://mycompany.my.salesforce.com"
                    />
                    <TextField
                      label={t("Org / Instance ID")}
                      size="small"
                      fullWidth
                      required
                      value={app.orgId || ""}
                      onChange={(e) =>
                        updateApplication(appIndex, {
                          ...app,
                          orgId: e.target.value,
                        })
                      }
                      placeholder="00D5g00000XYZ"
                    />
                  </Box>
                </Paper>
              )}

              {/* Repositories — shown for custom & hybrid */}
              {(app.type === "custom" || app.type === "hybrid") && (
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1.5}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <CodeOutlinedIcon
                        sx={{ fontSize: 16, color: "text.secondary" }}
                      />
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        fontSize="12px"
                      >
                        {t("Repositories")}
                      </Typography>
                      <Chip
                        label={`${app.repos.length} ${app.repos.length === 1 ? "repo" : "repos"}`}
                        size="small"
                        sx={{ fontSize: "9px", height: 18 }}
                      />
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                      onClick={() => addRepo(appIndex)}
                      sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        fontWeight: 600,
                        borderRadius: "6px",
                        py: 0.3,
                      }}
                    >
                      {t("Add Repo")}
                    </Button>
                  </Box>

                  {!configLoading && !isGitConfigured && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      <strong>{t("Git integration not configured.")}</strong>
                      <br />
                      {t("To enable repository selection")}{" "}
                      <span
                        onClick={() =>
                          navigate("/company-settings/settings/integration")
                        }
                        style={{
                          color: "blue",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {t("Click here")}
                      </span>{" "}
                      {t("and navigate to Company Settings → Integrations.")}
                    </Alert>
                  )}

                  {app.repos.length === 0 ? (
                    <Box
                      onClick={() => addRepo(appIndex)}
                      sx={{
                        border: (theme) =>
                          `2px dashed ${theme.palette.divider}`,
                        borderRadius: "8px",
                        p: 3,
                        textAlign: "center",
                        cursor: "pointer",
                        color: "text.secondary",
                        fontSize: "12px",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: (theme) => `${theme.palette.primary.main}05`,
                        },
                      }}
                    >
                      {t("Click to add the first repository")}
                    </Box>
                  ) : (
                    app.repos.map((repo, repoIndex) => (
                      <Paper
                        key={repoIndex}
                        elevation={0}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          borderRadius: "8px",
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.03)"
                              : "#f9fafb",
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={1}
                        >
                          <Chip
                            label={`Repo ${repoIndex + 1}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontSize: "10px", height: 20 }}
                          />
                          <Tooltip title={t("Remove repo")}>
                            <IconButton
                              size="small"
                              onClick={() => removeRepo(appIndex, repoIndex)}
                              sx={{ color: "error.main" }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box
                          display="grid"
                          gridTemplateColumns="1fr 1fr"
                          gap={1}
                          mb={1}
                        >
                          <Box display="flex" alignItems="center">
                            <Autocomplete
                              fullWidth
                              size="small"
                              disabled={repoDataLoading}
                              options={
                                repoData?.data?.decryptedData?.data || []
                              }
                              getOptionLabel={(option: any) =>
                                typeof option === "string"
                                  ? option
                                  : option?.clone_url ||
                                    option?.name ||
                                    option?.repositoryName ||
                                    option?.url ||
                                    ""
                              }
                              value={
                                (
                                  repoData?.data?.decryptedData?.data || []
                                ).find(
                                  (r: any) =>
                                    r?.clone_url === repo.url ||
                                    r?.name === repo.url ||
                                    r?.repositoryName === repo.url,
                                ) || null
                              }
                              onChange={(_, newValue: any) => {
                                const repoName =
                                  newValue?.name ||
                                  newValue?.repositoryName ||
                                  "";
                                const cloneUrl = newValue?.clone_url || "";
                                updateRepo(appIndex, repoIndex, {
                                  ...repo,
                                  url: cloneUrl || repoName,
                                  branch: "",
                                });
                                if (repoName) {
                                  fetchBranches(appIndex, repoIndex, repoName);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t("Select Repository")}
                                  placeholder={t("Select a repository")}
                                  variant="outlined"
                                  required
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                            {repoDataLoading && (
                              <CircularProgress size={16} sx={{ ml: 1 }} />
                            )}
                          </Box>
                          <Box display="flex" alignItems="center">
                            {(() => {
                              const branchKey = `${appIndex}-${repoIndex}`;
                              const branchInfo = branchDataMap[branchKey];
                              const branchOptions = branchInfo?.data || [];
                              const branchLoading =
                                branchInfo?.loading || false;
                              return (
                                <>
                                  <Autocomplete
                                    fullWidth
                                    size="small"
                                    disabled={!repo.url || branchLoading}
                                    options={branchOptions}
                                    getOptionLabel={(option: any) =>
                                      typeof option === "string"
                                        ? option
                                        : option?.name || ""
                                    }
                                    value={
                                      branchOptions.find(
                                        (b: any) => b?.name === repo.branch,
                                      ) || null
                                    }
                                    onChange={(_, newValue: any) => {
                                      updateRepo(appIndex, repoIndex, {
                                        ...repo,
                                        branch: newValue?.name || "",
                                      });
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t("Branch")}
                                        placeholder={t("Select branch")}
                                        variant="outlined"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    )}
                                  />
                                  {branchLoading && (
                                    <CircularProgress
                                      size={16}
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                        <TextField
                          label={t("Description")}
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={repo.description}
                          onChange={(e) =>
                            updateRepo(appIndex, repoIndex, {
                              ...repo,
                              description: e.target.value,
                            })
                          }
                          placeholder="e.g., Frontend React app — handles UI, auth flows"
                          helperText={
                            <Box
                              component="span"
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 0.8,
                                py: 0.2,
                                borderRadius: "3px",
                                background: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(139,92,246,0.08)"
                                    : "#f5f3ff",
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "8px",
                                  fontWeight: 800,
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "#a78bfa"
                                      : "#7c3aed",
                                }}
                              >
                                ✦ AI
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "9.5px",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "#c4b5fd"
                                      : "#6d28d9",
                                  fontWeight: 500,
                                }}
                              >
                                {t("Helps AI understand repo contents")}
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1 }}
                        />
                        <Box
                          display="grid"
                          gridTemplateColumns="1fr 1fr"
                          gap={1}
                        >
                          <TextField
                            label={t("Current Release Tag")}
                            size="small"
                            fullWidth
                            value={repo.currentRelease}
                            onChange={(e) =>
                              updateRepo(appIndex, repoIndex, {
                                ...repo,
                                currentRelease: e.target.value,
                              })
                            }
                            placeholder="release/v2.1"
                          />
                          <TextField
                            label={t("Incoming Release Tag")}
                            size="small"
                            fullWidth
                            value={repo.incomingRelease}
                            onChange={(e) =>
                              updateRepo(appIndex, repoIndex, {
                                ...repo,
                                incomingRelease: e.target.value,
                              })
                            }
                            placeholder="release/v2.2"
                          />
                        </Box>
                      </Paper>
                    ))
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </Paper>
      ))}

      {/* Add Another Application Button */}
      {applications.length > 0 && (
        <Box
          onClick={addApplication}
          sx={{
            border: (theme) => `1px dashed ${theme.palette.divider}`,
            borderRadius: "8px",
            p: 1.2,
            textAlign: "center",
            cursor: "pointer",
            color: "text.secondary",
            fontSize: "12px",
            fontWeight: 500,
            mt: 0.5,
            transition: "all 0.15s",
            "&:hover": {
              borderColor: "primary.main",
              color: "primary.main",
              bgcolor: (theme) => `${theme.palette.primary.main}05`,
            },
          }}
        >
          + {t("Add another application")}
        </Box>
      )}
    </Paper>
  );
}
